import React from 'react';
import { FmSplit } from './FmLayout';
import type { FmSplitAlign, FmSplitBalance, FmSplitSide, FmSplitStackOrder } from './FmLayout';
import type { FmDeviceTier } from './FmResponsive';
import './FmSplitSection.css';

export type FmSplitSectionSurface = 'plain' | 'framed';

export interface FmSplitSectionProps {
  id?: string;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /**
   * Semantic heading level for the section title. Defaults to `2` (h2). Set to
   * `1` for the single page-level headline (e.g. a marketing hero) so the route
   * exposes exactly one h1 for the document outline / SEO. Visual styling is
   * owned by `.fm-split-section__title` and does not change with the level.
   */
  headingLevel?: 1 | 2 | 3;
  body?: React.ReactNode;
  bullets?: string[];
  actions?: React.ReactNode;
  content?: React.ReactNode;
  graphic: React.ReactNode;
  tier?: FmDeviceTier;
  /** Desktop side of the graphic (FmSplit recipe semantics). */
  side?: FmSplitSide;
  /** Column recipe: balanced, media-dominant or copy-narrow. */
  balance?: FmSplitBalance;
  /** Vertical alignment of copy against the graphic. */
  align?: FmSplitAlign;
  /** Stacked reading order. */
  stackOrder?: FmSplitStackOrder;
  /** Keep the graphic in view while the copy scrolls (two-column layout only). */
  sticky?: boolean;
  surface?: FmSplitSectionSurface;
  graphicGlass?: boolean;
  footer?: React.ReactNode;
  /**
   * @internal DS/migration class hook; forbidden for route-owned layout, colour,
   * spacing, and section chrome. Use the semantic split props instead.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

function resolveSplitSection(args: { id?: string; dataTestId?: string }) {
  return {
    resolvedTestId: args.dataTestId ?? (args.id ? `fm-split-section-${args.id}` : undefined),
    headingId: args.id ? `${args.id}-heading` : undefined,
  };
}

function splitSectionClassName(args: {
  surface: FmSplitSectionSurface;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
}): string {
  return cx(
    'fm-split-section',
    `fm-split-section--${args.surface}`,
    args.className,
  );
}

interface SplitSectionCopyProps {
  headingId?: string;
  headingLevel: 1 | 2 | 3;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  bullets?: string[];
  content?: React.ReactNode;
  actions?: React.ReactNode;
}

const SplitSectionCopy: React.FC<SplitSectionCopyProps> = ({
  headingId,
  headingLevel,
  eyebrow,
  title,
  body,
  bullets,
  content,
  actions,
}) => (
  <div className="fm-split-section__copy">
    {eyebrow ? <span className="fm-split-section__eyebrow">{eyebrow}</span> : null}
    {React.createElement(
      `h${headingLevel}`,
      { id: headingId, className: 'fm-split-section__title' },
      title,
    )}
    {body ? <p className="fm-split-section__body">{body}</p> : null}
    {bullets && bullets.length > 0 ? (
      <ul className="fm-split-section__bullets">
        {bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    ) : null}
    {content && <div className="fm-split-section__content">{content}</div>}
    {actions ? <div className="fm-split-section__actions">{actions}</div> : null}
  </div>
);

const FmSplitSection: React.FC<FmSplitSectionProps> = ({
  id,
  eyebrow,
  title,
  headingLevel = 2,
  body,
  bullets,
  actions,
  content,
  graphic,
  tier,
  side = 'end',
  balance = 'balanced',
  align = 'start',
  stackOrder = 'content-first',
  sticky = false,
  surface = 'plain',
  graphicGlass = false,
  footer,
  className,
  'data-testid': dataTestId,
}) => {
  const { resolvedTestId, headingId } = resolveSplitSection({ id, dataTestId });
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={splitSectionClassName({ surface, className })}
      data-testid={resolvedTestId}
    >
      <FmSplit
        tier={tier}
        side={side}
        balance={balance}
        align={align}
        stackOrder={stackOrder}
        sticky={sticky}
        graphicGlass={graphicGlass}
        contentSurface={surface === 'framed' ? 'solid' : 'plain'}
        data-testid={resolvedTestId ? `${resolvedTestId}-split` : undefined}
        content={
          <SplitSectionCopy
            headingId={headingId}
            headingLevel={headingLevel}
            eyebrow={eyebrow}
            title={title}
            body={body}
            bullets={bullets}
            content={content}
            actions={actions}
          />
        }
        graphic={graphic}
      />
      {footer ? <div className="fm-split-section__footer">{footer}</div> : null}
    </section>
  );
};

export { FmSplitSection };
export default FmSplitSection;
